import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { Search, ArrowRightOutlined as ArrowRight } from "@mui/icons-material";
import { Search as SearchIcon, Clear as ClearIcon } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import jsPDF from "jspdf";
import {
  OutlinedInput,
  IconButton,
  InputAdornment,
  Button,
  CircularProgress,
  Typography,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Paper,
  Tooltip,
  TextField,
  useTheme,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { ZoomIn as ViewIcon, Download as DownloadIcon, Edit as EditIcon } from "@mui/icons-material";
import {
  getMachines,
  readAsyncStorageValues,
  getAllServiceTickets,
  getAllOldServiceTickets,
  changePriorityForServiceTicket,
  // getServiceTicketsByStatusListAndScheduledBefore,
} from "features/service/service.slice";
import { Links } from "utils/pageLinkNames";
import DataGridTable from "component/DataGridTable";
import { DataGrid } from "@mui/x-data-grid";
import { exportToCsv } from "utils/utilFunctions";
import { toast } from "react-toastify";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import TableSkeleton from "component/Loaders/TableSkeleton";
import FilterHeaderDropdown from "component/ui/FilterHeaderDropdown";
import ColumnHeaderLabel from "component/ui/ColumnHeaderLabel";

const ServiceTicketsTab = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const { mode } = useSelector((state) => state.globalState);

  const [searchParams, setSearchParams] = useSearchParams();
  const [serviceTickets, setServiceTickets] = useState([]);
  const [showExportAlertDialog, setShowExportAlertDialog] = useState(false);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [scheduleMonthValue, setScheduleMonthValue] = useState(moment().startOf("month").format("YYYY-MM-DD"));

  const [searchValue, setSearchValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedTab, setSelectedTab] = useState(1);
  const { user } = useSelector((state) => state.auth);

  const [filteredCounts, setFilteredCounts] = useState({
    all: 0,
    periodic: 0,
    oneTime: 0,
    old: 0,
  });

  const [filters, setFilters] = useState({
    creator: "",
    assigner: "",
    technician: "",
    maintenanceCode: "",
    category: "",
    status: "",
    manufModel: "",
    vendor: "",
  });

  const [filterOptions, setFilterOptions] = useState({
    overDue: false,
    upComingIn: null,
    status: [],
    category: [],
    manufModel: [],
    assigner: [],
    creator: [],
    technician: [],
    maintenanceCode: [],
  });

  const [filteredData, setFilteredData] = useState([]);

  const { machinesData, appConstants, isLoading, allServiceTickets, allOldServiceTickets, serviceTicketsByStatusListAndScheduledBefore } =
    useSelector((state) => state.service);

  const scheduleMonthOptions = useMemo(() => {
    const base = moment().startOf("month");
    const items = [base.clone().subtract(1, "month"), base.clone(), base.clone().add(1, "month")];
    return items.map((m) => ({
      value: m.format("YYYY-MM-DD"),
      label: m.format("MMMM YYYY"),
    }));
  }, []);

  const TAB_CONFIG = {
    1: { key: "all", categories: ["EQUIPMENT_SUPPORT", "TASK", "CALIBRATION", "INSPECTION", "INTERNAL_AUDIT", "PREVENTIVE_MAINTENANCE"] },
    2: { key: "periodic", categories: ["CALIBRATION", "INSPECTION", "INTERNAL_AUDIT", "PREVENTIVE_MAINTENANCE"] },
    3: { key: "oneTime", categories: ["EQUIPMENT_SUPPORT", "TASK"] },
    4: { key: "old", categories: [] },
  };

  useEffect(() => {
    dispatch(readAsyncStorageValues());
    dispatch(getMachines());
    dispatch(getAllServiceTickets());
    dispatch(getAllOldServiceTickets());
  }, [dispatch]);

  const updatePriority = (ticketId, newPriority) => {
    const updateObj = {
      serviceTktId: ticketId,
      Priority: newPriority,
    };
    dispatch(changePriorityForServiceTicket(updateObj)).then((res) => {
      if (res && res.error) {
        toast.error("Failed to update priority!");
      } else if (res && res.payload?.status === 200) {
        toast.success(`Priority updated to ${newPriority} successfully!`, {
          autoClose: 1000,
        });
        dispatch(getAllServiceTickets());
      } else {
        toast.error("Something went wrong!");
      }
    });
  };

  useEffect(() => {
    if (selectedTab === 4 && allOldServiceTickets?.data) loadData(4);
  }, [selectedTab, allOldServiceTickets?.data]);

  useEffect(() => {
    if (allServiceTickets?.data || allOldServiceTickets?.data) {
      loadData(selectedTab);
    }
  }, [selectedTab, allServiceTickets, allOldServiceTickets]);

  const getVendorDisplayName = useCallback(
    (ticket) => {
      const vendorId = ticket?.vendor?.id ?? ticket?.vendor;
      const customer = appConstants?.customers?.find((c) => c.id === vendorId);
      return customer?.customerName || "Internal";
    },
    [appConstants?.customers],
  );

  const ticketMatchesFilters = (ticket, assigneUser, assignerUser, creatorUser) => {
    const filters = [
      () => !filterOptions.maintenanceCode?.length || filterOptions.maintenanceCode.includes(ticket?.maintenanceCode),
      () => !filterOptions.category?.length || filterOptions.category.includes(ticket?.ticketCategory),
      () => !filterOptions.status?.length || filterOptions.status.includes(ticket?.status),
      () => !filterOptions.ticketIds?.length || filterOptions.ticketIds.includes(ticket?.ticketId.toString()),
      () => !filterOptions.creator?.length || filterOptions.creator.includes(creatorUser?.name),
      () => !filterOptions.assigner?.length || filterOptions.assigner.includes(assignerUser?.nameEmpno),
      () => !filterOptions.technician?.length || filterOptions.technician.includes(assigneUser?.nameEmpno),
      () => !filterOptions.modelName?.length || filterOptions.modelName.includes(ticket?.modelName),
      () => !filterOptions.manufacturer?.length || filterOptions.manufacturer.includes(ticket?.manufacturer),
      () => {
        if (!filterOptions.manufModel?.length) return true;
        const manufacturer = ticket?.manufacturer || "";
        const modelName = ticket?.modelName || "";
        const combinedValue = manufacturer && modelName ? `${manufacturer} - ${modelName}` : manufacturer || modelName;
        return filterOptions.manufModel.includes(combinedValue);
      },
      () =>
        !filterOptions.upComingIn ||
        (ticket.dueDate && moment(ticket.dueDate).isBetween(moment(), moment().add(parseInt(filterOptions.upComingIn), "d"))),
      () => !filterOptions.upComingIn || ticket.dueDate,
      () => !filterOptions.overDue || moment(moment().format("DD MMM YYYY")).isAfter(moment(ticket.dueDate).format("DD MMM YYYY")),
    ];
    return filters.every((filter) => filter());
  };

  const ticketBelongsToTab = (ticket, tabNumber) => {
    if (tabNumber === 4) return true;

    const config = TAB_CONFIG[tabNumber];
    const categories = config?.categories.map((cat) => appConstants.allTypes.SERVICE_CATEGORY[cat]);
    return categories?.includes(ticket.ticketCategory);
  };

  const transformTicket = (ticket) => {
    const assigneUser = appConstants?.appUsers?.find((user) => ticket?.assigneeId === user.userId);
    //const assignerUser = appConstants?.appUsers?.find((user) => ticket?.assignerId === user.userId);
    const assignerUser = appConstants?.appUsers?.find(
      (user) => ticket?.assignerId === user.userId && user.userRole === appConstants.allTypes.USER_ROLES.ROLE_MAINTENANCE_SUPERVISOR,
    );

    const creatorUser = appConstants?.appUsers?.find((user) => ticket?.creatorId === user.userId);

    return {
      ticketId: ticket?.ticketId,
      ticketNumber: appConstants?.showServiceTicketId ? ticket?.ticketId : ticket?.ticketNumber || ticket?.ticketId,
      ticketCategory: ticket.ticketCategory,
      status: ticket.status,
      machineManufacturer: ticket?.manufacturer || "",
      machineModel: ticket?.modelName || "",
      machineModelId: ticket?.modelId || "",
      machineId: ticket?.machineId || "",
      machineSerialNumber: ticket?.serialNumber || "",
      machineMaintenanceCode: ticket?.maintenanceCode || "",
      description: ticket?.description || "",
      dueDate: ticket.dueDate ? moment(ticket.dueDate).format("DD MMM YY") : null,
      dateCreated: moment(ticket.openDate).format("DD MMM YY"),
      scheduledDate: moment(ticket.scheduledDate).format("DD MMM YY"),
      priority: ticket.priority || null,
      vendor: ticket.vendor || null,
      creator: creatorUser?.name || "",
      assigner: assignerUser?.name || "",
      technician: assigneUser?.name || "",
      safetyTicket: ticket?.safetyTicket || null,
    };
  };

  const rawTicketsForTab = useMemo(() => {
    const data = selectedTab === 4 ? allOldServiceTickets?.data || [] : allServiceTickets?.data || [];
    return data.filter((ticket) => ticketBelongsToTab(ticket, selectedTab));
  }, [selectedTab, allServiceTickets, allOldServiceTickets, appConstants]);

  const filteredTickets = useMemo(() => {
    let tickets = rawTicketsForTab || [];
    if (filters.creator) {
      tickets = tickets.filter((t) => {
        const creatorUser = appConstants?.appUsers?.find((u) => t.creatorId === u.userId);
        return (creatorUser?.name || "") === filters.creator;
      });
    }
    if (filters.assigner) {
      tickets = tickets.filter((t) => {
        const assignerUser = appConstants?.appUsers?.find(
          (u) => t.assignerId === u.userId && u.userRole === appConstants.allTypes.USER_ROLES.ROLE_MAINTENANCE_SUPERVISOR,
        );
        return (assignerUser?.nameEmpno || assignerUser?.name || "") === filters.assigner;
      });
    }
    if (filters.technician) {
      tickets = tickets.filter((t) => {
        const assigneUser = appConstants?.appUsers?.find((u) => t.assigneeId === u.userId);
        return (assigneUser?.nameEmpno || assigneUser?.name || "") === filters.technician;
      });
    }
    if (filters.maintenanceCode) {
      tickets = tickets.filter((t) => (t.maintenanceCode || "") === filters.maintenanceCode);
    }
    if (filters.category) {
      tickets = tickets.filter((t) => (t.ticketCategory || "") === filters.category);
    }
    if (filters.status) {
      tickets = tickets.filter((t) => (t.status || "") === filters.status);
    }
    if (filters.manufModel) {
      tickets = tickets.filter((t) => {
        const manufacturer = t.manufacturer || "";
        const modelName = t.modelName || "";
        const combined = manufacturer && modelName ? `${manufacturer} - ${modelName}` : manufacturer || modelName;
        return combined === filters.manufModel;
      });
    }
    if (filters.vendor) {
      tickets = tickets.filter((t) => getVendorDisplayName(t) === filters.vendor);
    }
    if (filterOptions.overDue) {
      tickets = tickets.filter((t) => t.dueDate && moment(moment().format("DD MMM YYYY")).isAfter(moment(t.dueDate).format("DD MMM YYYY")));
    }
    if (filterOptions.upComingIn != null) {
      tickets = tickets.filter(
        (t) => t.dueDate && moment(t.dueDate).isBetween(moment(), moment().add(parseInt(filterOptions.upComingIn), "d")),
      );
    }
    if (filterOptions.ticketIds?.length) {
      tickets = tickets.filter((t) => filterOptions.ticketIds.includes(t.ticketId?.toString()));
    }
    if (searchValue.trim()) {
      const searchLower = searchValue.toLowerCase();
      tickets = tickets.filter((t) => {
        const assigneUser = appConstants?.appUsers?.find((u) => t.assigneeId === u.userId);
        const assignerUser = appConstants?.appUsers?.find(
          (u) => t.assignerId === u.userId && u.userRole === appConstants.allTypes.USER_ROLES.ROLE_MAINTENANCE_SUPERVISOR,
        );
        const creatorUser = appConstants?.appUsers?.find((u) => t.creatorId === u.userId);
        const searchFields = [
          t.ticketId?.toString(),
          t.ticketNumber?.toString(),
          t.description,
          t.maintenanceCode,
          t.manufacturer,
          t.modelName,
          assigneUser?.name,
          assignerUser?.name,
          creatorUser?.name,
        ];
        return searchFields.some((field) => field?.toLowerCase().includes(searchLower));
      });
    }
    return tickets;
  }, [rawTicketsForTab, filters, filterOptions.overDue, filterOptions.upComingIn, filterOptions.ticketIds, searchValue, appConstants, getVendorDisplayName]);

  const { creators, supervisors, technicians, maintenanceCodes, categories, statuses, manufModels, vendors } = useMemo(() => {
    const creatorsSet = new Set();
    const supervisorsSet = new Set();
    const techniciansSet = new Set();
    const maintenanceCodesSet = new Set();
    const categoriesSet = new Set();
    const statusesSet = new Set();
    const manufModelsSet = new Set();
    const vendorsSet = new Set();
    (filteredTickets || []).forEach((ticket) => {
      const creatorUser = appConstants?.appUsers?.find((u) => ticket.creatorId === u.userId);
      if (creatorUser?.name) creatorsSet.add(creatorUser.name);
      const assignerUser = appConstants?.appUsers?.find(
        (u) => ticket.assignerId === u.userId && u.userRole === appConstants.allTypes.USER_ROLES.ROLE_MAINTENANCE_SUPERVISOR,
      );
      if (assignerUser) supervisorsSet.add(assignerUser.nameEmpno || assignerUser.name);
      const assigneUser = appConstants?.appUsers?.find((u) => ticket.assigneeId === u.userId);
      if (assigneUser) techniciansSet.add(assigneUser.nameEmpno || assigneUser.name);
      if (ticket.maintenanceCode) maintenanceCodesSet.add(ticket.maintenanceCode);
      if (ticket.ticketCategory) categoriesSet.add(ticket.ticketCategory);
      if (ticket.status) statusesSet.add(ticket.status);
      if (ticket.manufacturer && ticket.modelName) manufModelsSet.add(`${ticket.manufacturer} - ${ticket.modelName}`);
      vendorsSet.add(getVendorDisplayName(ticket));
    });
    return {
      creators: Array.from(creatorsSet).sort((a, b) => a.localeCompare(b)),
      supervisors: Array.from(supervisorsSet).sort((a, b) => a.localeCompare(b)),
      technicians: Array.from(techniciansSet).sort((a, b) => a.localeCompare(b)),
      maintenanceCodes: Array.from(maintenanceCodesSet).sort((a, b) => a.localeCompare(b)),
      categories: Array.from(categoriesSet).sort((a, b) => a.localeCompare(b)),
      statuses: Array.from(statusesSet).sort((a, b) => a.localeCompare(b)),
      manufModels: Array.from(manufModelsSet).sort((a, b) => a.localeCompare(b)),
      vendors: Array.from(vendorsSet).sort((a, b) => a.localeCompare(b)),
    };
  }, [filteredTickets, appConstants, getVendorDisplayName]);

  const gridRows = useMemo(() => {
    const rows = (filteredTickets || []).map(transformTicket);
    return rows.sort((a, b) => b.ticketId - a.ticketId);
  }, [filteredTickets, appConstants]);

  const loadData = (selectedTab) => {
    setLoading(true);
    const ticketsToProcess = selectedTab === 4 ? allOldServiceTickets?.data : allServiceTickets?.data;

    if (!ticketsToProcess) {
      setLoading(false);
      return;
    }

    const serviceTickets = ticketsToProcess
      .filter((ticket) => {
        const assigneUser = appConstants?.appUsers?.find((user) => ticket?.assigneeId === user.userId);
        return ticketMatchesFilters(ticket, assigneUser) && ticketBelongsToTab(ticket, selectedTab);
      })
      .map(transformTicket)
      .sort((a, b) => b.ticketId - a.ticketId);

    setServiceTickets(serviceTickets);
    updateFilteredCounts();
    setLoading(false);
  };
  const hasActiveFilters = () => {
    return (
      searchValue.trim() !== "" ||
      filterOptions.overDue ||
      filterOptions.upComingIn != null ||
      (filterOptions.ticketIds && filterOptions.ticketIds.length > 0) ||
      Object.values(filters).some((v) => v !== "")
    );
  };

  const getCountForTab = (tabNumber) => {
    const data = tabNumber === 4 ? allOldServiceTickets?.data : allServiceTickets?.data;
    if (!data) return 0;

    return data.filter((ticket) => ticketBelongsToTab(ticket, tabNumber)).length;
  };

  const initializeAllCounts = () => {
    if (allServiceTickets?.data && appConstants?.allTypes?.SERVICE_CATEGORY) {
      setFilteredCounts({
        all: getCountForTab(1),
        periodic: getCountForTab(2),
        oneTime: getCountForTab(3),
        old: getCountForTab(4),
      });
    }
  };

  const updateFilteredCounts = () => {
    if (!allServiceTickets?.data || !appConstants?.allTypes?.SERVICE_CATEGORY) return;

    const tabKey = TAB_CONFIG[selectedTab]?.key;

    if (!hasActiveFilters()) {
      initializeAllCounts();
      return;
    }

    setFilteredCounts((prev) => ({ ...prev, [tabKey]: filteredTickets.length }));
  };

  useEffect(() => {
    if (allServiceTickets?.data?.length) {
      initializeAllCounts();
    }
  }, [allServiceTickets]);

  useEffect(() => {
    if (allServiceTickets?.data || allOldServiceTickets?.data) {
      updateFilteredCounts();
    }
  }, [selectedTab, allServiceTickets, allOldServiceTickets, filterOptions, searchValue, filters, filteredTickets.length]);

  useEffect(() => {
    const ticketCategoryArrayParam = searchParams.getAll("ticketCategory[]");
    const maintenanceCodeArrayParam = searchParams.getAll("maintenanceCode[]");
    const ticketStatusArrayParam = searchParams.getAll("ticketStatus[]");
    const ticketIdArrayParam = searchParams.getAll("ticketId[]");
    setFilterOptions({
      ...filterOptions,
      category: ticketCategoryArrayParam,
      maintenanceCode: maintenanceCodeArrayParam,
      status: ticketStatusArrayParam,
      ticketIds: ticketIdArrayParam,
    });
  }, []);

  const getCurrentTabData = () => {
    const data = selectedTab === 4 ? allOldServiceTickets?.data || [] : allServiceTickets?.data || [];
    return data.filter((ticket) => ticketBelongsToTab(ticket, selectedTab));
  };

  const getMonthOptions = () => {
    const options = [];
    const currentDate = moment();

    const prevMonth = moment(currentDate).subtract(1, "month");
    options.push({
      value: prevMonth.format("YYYY-MM"),
      label: prevMonth.format("MMMM YYYY"),
    });

    options.push({
      value: currentDate.format("YYYY-MM"),
      label: currentDate.format("MMMM YYYY"),
    });

    const nextMonth = moment(currentDate).add(1, "month");
    options.push({
      value: nextMonth.format("YYYY-MM"),
      label: nextMonth.format("MMMM YYYY"),
    });

    return options;
  };

  const renderGridActions = (data) => {
    return (
      <div>
        {/* <IconButton sx={{ mr: 1 }} onClick={() => navigate(`/${Links.SERVICE_TICKET}/` + data.row.ticketId)}>
          <Tooltip title="View">
            <ViewIcon sx={{ color: mode === "dark" ? "#fff" : theme.palette.primary[50] }} />
          </Tooltip>
        </IconButton> */}
        <IconButton
          sx={{ mr: 1 }}
          onClick={() => navigate(`/${Links.UPDATE_SERVICE_TICKET}/` + data.row.ticketId, { state: { enableEditing: true } })}
        >
          <Tooltip title="Edit">
            <EditIcon sx={{ color: mode === "dark" ? "#fff" : theme.palette.primary[50] }} />
          </Tooltip>
        </IconButton>
        {data.row.priority === "High" && (
          <Tooltip title="Medium Priority">
            <IconButton sx={{ mr: 1 }} onClick={() => updatePriority(data.row.ticketId, "Medium")}>
              <ArrowDownwardIcon sx={{ color: mode === "dark" ? "#fff" : theme.palette.primary[50] }} />
            </IconButton>
          </Tooltip>
        )}
        {data.row.priority === "Medium" && (
          <span>
            <Tooltip title="High Priority">
              <IconButton onClick={() => updatePriority(data.row.ticketId, "High")}>
                <ArrowUpwardIcon sx={{ color: mode === "dark" ? "#fff" : theme.palette.primary[50] }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Low Priority">
              <IconButton onClick={() => updatePriority(data.row.ticketId, "Low")}>
                <ArrowDownwardIcon sx={{ color: mode === "dark" ? "#fff" : theme.palette.primary[50] }} />
              </IconButton>
            </Tooltip>
          </span>
        )}

        {data.row.priority === "Low" && (
          <Tooltip title="Medium Priority">
            <IconButton onClick={() => updatePriority(data.row.ticketId, "Medium")}>
              <ArrowUpwardIcon sx={{ color: mode === "dark" ? "#fff" : theme.palette.primary[50] }} />
            </IconButton>
          </Tooltip>
        )}
      </div>
    );
  };

  const handleSearchChange = (event) => setSearchValue(event.target.value);

  const handleClearSearch = () => setSearchValue("");

  const handleTabsChange = (event, newValue) => {
    setSelectedTab(newValue);
    handleClearSearch();
    setFilters({
      creator: "",
      assigner: "",
      technician: "",
      maintenanceCode: "",
      category: "",
      status: "",
      manufModel: "",
      vendor: "",
    });
    setFilterOptions({
      overDue: false,
      upComingIn: null,
      status: [],
      category: [],
      manufModel: [],
      assigner: [],
      creator: [],
      technician: [],
      maintenanceCode: [],
      ticketIds: [],
    });
  };

  const clearAllFilters = () => {
    setFilters({
      creator: "",
      assigner: "",
      technician: "",
      maintenanceCode: "",
      category: "",
      status: "",
      manufModel: "",
      vendor: "",
    });
    setFilterOptions({
      maintenanceCode: [],
      manufModel: [],
      creator: [],
      assigner: [],
      technician: [],
      category: [],
      status: [],
      upComingIn: null,
      overDue: false,
      ticketIds: [],
    });
    setSearchValue("");
  };

  const formatShortDate = (dateVal) => {
    if (!dateVal) return "";
    const m = moment(dateVal);
    return m.isValid() ? m.format("DD MMM YY") : "";
  };

  const generateScheduledTicketsPdf = (monthStartValue) => {
    if (!appConstants?.allTypes) {
      toast.error("Unable to generate schedule. Please try again.");
      return;
    }

    const { ASSIGNED, ASSIGN_SERVICE_ENGINEER } = appConstants.allTypes.SERVICE_TICKET_STATUS || {};
    const monthStart = moment(monthStartValue).startOf("month");
    const monthEnd = monthStart.clone().endOf("month");

    const current = Array.isArray(allServiceTickets?.data) ? allServiceTickets.data : [];
    const old = Array.isArray(allOldServiceTickets?.data) ? allOldServiceTickets.data : [];
    const combined = [...current, ...old];

    const byId = new Map();
    for (const t of combined) {
      const id = t?.ticketId ?? t?.id;
      if (id == null) continue;
      if (!byId.has(String(id))) byId.set(String(id), t);
    }
    const tickets = [...byId.values()];

    const inMonthAndStatus = tickets.filter((t) => {
      const status = t?.status;
      const sched = t?.scheduledDate;
      const schedMoment = sched ? moment(sched) : null;
      if (!schedMoment || !schedMoment.isValid()) return false;
      const isSchedInMonth = schedMoment.isBetween(monthStart, monthEnd, "day", "[]");
      const isWantedStatus = [ASSIGNED, ASSIGN_SERVICE_ENGINEER].includes(status);
      return isSchedInMonth && isWantedStatus;
    });

    if (!inMonthAndStatus.length) {
      toast.info("No scheduled tickets found for the selected month.");
      return;
    }

    const rows = inMonthAndStatus
      .map((ticket) => {
        const assigneUser = appConstants?.appUsers?.find((u) => ticket?.assigneeId === u.userId);
        const assignerUser = appConstants?.appUsers?.find(
          (u) => ticket?.assignerId === u.userId && u.userRole === appConstants.allTypes.USER_ROLES.ROLE_MAINTENANCE_SUPERVISOR,
        );
        const creatorUser = appConstants?.appUsers?.find((u) => ticket?.creatorId === u.userId);

        return {
          ticketId: appConstants?.showServiceTicketId ? ticket?.ticketId : ticket?.ticketNumber || ticket?.ticketId,
          openDate: formatShortDate(ticket?.openDate),
          scheduledDate: formatShortDate(ticket?.scheduledDate),
          dueDate: formatShortDate(ticket?.dueDate),
          creator: creatorUser?.name || "",
          supervisor: assignerUser?.name || assignerUser?.nameEmpno || "",
          technician: assigneUser?.name || "",
          manufModel: [ticket?.manufacturer || "", ticket?.modelName || ""].filter(Boolean).join(" - "),
          maintenanceCode: ticket?.maintenanceCode || "",
          category: appConstants?.allTypes?.ABBREVIATIONS?.[ticket?.ticketCategory] || ticket?.ticketCategory || "",
          status: ticket?.status || "",
          priority: ticket?.priority || "",
        };
      })
      .sort((a, b) => moment(a.scheduledDate, "DD MMM YY").diff(moment(b.scheduledDate, "DD MMM YY")));

    const title = `Scheduled Tickets - ${monthStart.format("MMMM YYYY")}`;

    const doc = new jsPDF({
      orientation: "landscape",
      unit: "pt",
      format: "a4",
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const marginX = 10;
    const marginTop = 24;
    const marginBottom = 24;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text(title, marginX, marginTop);

    const columns = [
      { header: "Ticket ID", key: "ticketId", width: 45 },
      { header: "Open Date", key: "openDate", width: 52 },
      { header: "Sch Date", key: "scheduledDate", width: 48 },
      { header: "Due Date", key: "dueDate", width: 48 },
      { header: "Creator", key: "creator", width: 109 },
      { header: "Supervisor", key: "supervisor", width: 109 },
      { header: "Assignee", key: "technician", width: 90 },
      { header: "Manuf & Model", key: "manufModel", width: 179 },
      { header: "Maint. Code", key: "maintenanceCode", width: 134 },
      { header: "Category", key: "category", width: 48 },
      // { header: "Status", key: "status", width: 78 },
      // { header: "Priority", key: "priority", width: 56 },
    ];

    const totalWidth = columns.reduce((sum, c) => sum + c.width, 0);
    const scale = totalWidth > pageWidth - marginX * 2 ? (pageWidth - marginX * 2) / totalWidth : 1;
    const scaledCols = columns.map((c) => ({ ...c, width: Math.floor(c.width * scale) }));
    const tableX = marginX;
    const tableYStart = marginTop + 20;
    const headerHeight = 22;
    const rowHeight = 20;

    const truncate = (text, maxWidth) => {
      const s = String(text ?? "");
      if (!s) return "";
      if (doc.getTextWidth(s) <= maxWidth) return s;
      let out = s;
      while (out.length > 0 && doc.getTextWidth(out + "…") > maxWidth) {
        out = out.slice(0, -1);
      }
      return out ? out + "…" : "";
    };

    const drawHeader = (y) => {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8.5);
      let x = tableX;
      for (const col of scaledCols) {
        doc.rect(x, y, col.width, headerHeight);
        doc.text(truncate(col.header, col.width - 6), x + 3, y + 12);
        x += col.width;
      }
      return y + headerHeight;
    };

    const drawRow = (y, row) => {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      let x = tableX;
      for (const col of scaledCols) {
        const val = row[col.key];
        doc.rect(x, y, col.width, rowHeight);
        doc.text(truncate(val, col.width - 6), x + 3, y + 11);
        x += col.width;
      }
      return y + rowHeight;
    };

    let y = drawHeader(tableYStart);
    for (const row of rows) {
      if (y + rowHeight > pageHeight - marginBottom) {
        doc.addPage();
        doc.setFont("helvetica", "bold");
        doc.setFontSize(14);
        doc.text(title, marginX, marginTop);
        y = drawHeader(tableYStart);
      }
      y = drawRow(y, row);
    }

    doc.save(`Scheduled_Tickets_${monthStart.format("MMM_YYYY")}.pdf`);
  };

  const dateComparator = (v1, v2) => moment(v1).diff(moment(v2));
  const columnAttributes = [
    {
      field: "ticketNumber",
      headerName: "Ticket ID",
      minWidth: 72,
      flex: 0.45,
      renderHeader: () => <ColumnHeaderLabel label="Ticket ID" />,
      valueGetter: (params) => {
        return appConstants?.showServiceTicketId ? params.row.ticketId : params.row.ticketNumber;
      },
      renderCell: (params) => {
        const displayValue = appConstants?.showServiceTicketId ? params.row.ticketId : params.value;
        return (
          <span
            style={{
              cursor: "pointer",
              textDecoration: "underline",
            }}
            onClick={() => navigate(`/${Links.SERVICE_TICKET}/` + params.row.ticketId)}
          >
            {displayValue}
          </span>
        );
      },
    },
    {
      field: "dateCreated",
      headerName: "Open Date",
      minWidth: 80,
      flex: 0.3,
      sortComparator: dateComparator,
      renderHeader: () => <ColumnHeaderLabel label="Open Date" />,
    },
    {
      field: "scheduledDate",
      headerName: "Sch. Date",
      minWidth: 77,
      flex: 0.3,
      sortComparator: dateComparator,
      renderHeader: () => <ColumnHeaderLabel label="Sch. Date" />,
    },
    {
      field: "dueDate",
      headerName: "Due Date",
      minWidth: 77,
      flex: 0.3,
      sortComparator: dateComparator,
      renderHeader: () => <ColumnHeaderLabel label="Due Date" />,
    },
    {
      field: "creator",
      headerName: "Creator",
      minWidth: 107,
      flex: 0.6,
      sortable: false,
      renderHeader: () => (
        <FilterHeaderDropdown
          label="Creator"
          value={filters.creator}
          options={creators}
          onChange={(value) => setFilters({ ...filters, creator: value })}
        />
      ),
    },
    {
      field: "assigner",
      headerName: "Supervisor",
      minWidth: 124,
      flex: 0.5,
      sortable: false,
      renderHeader: () => (
        <FilterHeaderDropdown
          label="Supervisor"
          value={filters.assigner}
          options={supervisors}
          onChange={(value) => setFilters({ ...filters, assigner: value })}
        />
      ),
    },
    {
      field: "technician",
      headerName: "Tech",
      minWidth: 100,
      flex: 0.3,
      sortable: false,
      renderHeader: () => (
        <FilterHeaderDropdown
          label="Tech"
          value={filters.technician}
          options={technicians}
          onChange={(value) => setFilters({ ...filters, technician: value })}
        />
      ),
    },
    ...(appConstants?.showManufacturerModelInAssetList === true
      ? [
          {
            field: "manufModel",
            headerName: "Manufacturer & Model",
            minWidth: 100,
            flex: 0.7,
            sortable: false,
            renderHeader: () => (
              <FilterHeaderDropdown
                label="Manf & Model"
                value={filters.manufModel}
                options={manufModels}
                onChange={(value) => setFilters({ ...filters, manufModel: value })}
              />
            ),
            renderCell: (params) => {
              const manufacturer = params.row.machineManufacturer || params.row.manufacturer || "";
              const modelName = params.row.machineModel || params.row.modelName || "";
              const combinedValue = manufacturer && modelName ? `${manufacturer} - ${modelName}` : manufacturer || modelName;

              return (
                <div
                  onClick={() => navigate(`/${Links.MACHINE_MODEL}/${params.row.machineModelId}`)}
                  style={{ cursor: "pointer", textDecoration: "none" }}
                  onMouseEnter={(e) => (e.currentTarget.style.textDecoration = "underline")}
                  onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}
                >
                  {combinedValue}
                </div>
              );
            },
            valueGetter: (params) => {
              const manufacturer = params.row.machineManufacturer || params.row.manufacturer || "";
              const modelName = params.row.machineModel || params.row.modelName || "";
              return manufacturer && modelName ? `${manufacturer} - ${modelName}` : manufacturer || modelName;
            },
          },
        ]
      : []),
    {
      field: "machineMaintenanceCode",
      headerName: "Maint. Code",
      editType: "dropdownedit",
      textAlign: "left",
      minWidth: 100,
      flex: 0.6,
      sortable: false,
      renderHeader: () => (
        <FilterHeaderDropdown
          label="Maint. Code"
          value={filters.maintenanceCode}
          options={maintenanceCodes}
          onChange={(value) => setFilters({ ...filters, maintenanceCode: value })}
        />
      ),
      renderCell: (params) => {
        return (
          <div
            onClick={() => navigate(`/${Links.EDIT_MACHINE}/${params.row.machineId}`)}
            onMouseEnter={(e) => (e.currentTarget.style.textDecoration = "underline")}
            onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}
          >
            {params.value}
          </div>
        );
      },
    },
    {
      field: "description",
      headerName: "Description",
      editType: "dropdownedit",
      textAlign: "left",
      minWidth: 90,
      flex: 0.35,
      renderHeader: () => <ColumnHeaderLabel label="Description" />,
    },
    {
      field: "ticketCategory",
      headerName: "Category",
      minWidth: 90,
      flex: 0.55,
      sortable: false,
      renderCell: (params) => {
        const abbreviation = appConstants?.allTypes?.ABBREVIATIONS?.[params.value];
        return abbreviation || params.value;
      },
      renderHeader: () => {
        const abbreviatedOptions = categories.map((cat) => appConstants?.allTypes?.ABBREVIATIONS?.[cat] || cat);
        const displayValue = filters.category ? appConstants?.allTypes?.ABBREVIATIONS?.[filters.category] || filters.category : "";
        return (
          <FilterHeaderDropdown
            label="Category"
            value={displayValue}
            options={abbreviatedOptions}
            onChange={(abbreviatedValue) => {
              if (!abbreviatedValue) {
                setFilters({ ...filters, category: "" });
                return;
              }
              const originalCategory =
                categories.find((cat) => appConstants?.allTypes?.ABBREVIATIONS?.[cat] === abbreviatedValue) || abbreviatedValue;
              setFilters({ ...filters, category: originalCategory });
            }}
          />
        );
      },
    },
    {
      field: "status",
      headerName: "Status",
      minWidth: 90,
      flex: 0.4,
      sortable: false,
      renderHeader: () => (
        <FilterHeaderDropdown
          label="Status"
          value={filters.status}
          options={statuses}
          onChange={(value) => setFilters({ ...filters, status: value })}
        />
      ),
    },
    {
      field: "vendor",
      headerName: "Vendor",
      minWidth: 100,
      flex: 0.4,
      sortable: false,
      renderHeader: () => (
        <FilterHeaderDropdown
          label="Vendor"
          value={filters.vendor}
          options={vendors}
          onChange={(value) => setFilters({ ...filters, vendor: value })}
        />
      ),
      valueGetter: (params) => getVendorDisplayName(params.row),
    },
    {
      field: "Actions",
      headerName: "Actions",
      width: 160,
      minWidth: 100,
      maxWidth: 140,
      sortable: false,
      filterable: false,
      renderHeader: () => <ColumnHeaderLabel label="Actions" />,
      renderCell: renderGridActions,
      disableClickEventBubbling: true,
    },
  ];
  const downloadCsv = (e, exportAll = false) => {
    e.preventDefault();
    const serviceTicketsList = [];
    if (exportAll) {
      const baseTickets = rawTicketsForTab || [];
      baseTickets.forEach((ticket) => {
        const machine = machinesData.find((machine) => machine.id === ticket?.machine?.id);
        const assigneUser = appConstants?.appUsers?.find((user) => {
          return ticket?.assignees?.[0] === user.userId;
        });
        const assignerUser = appConstants?.appUsers?.find((user) => ticket?.assignerId === user.userId);
        const creatorUser = appConstants?.appUsers?.find((user) => ticket?.creatorId === user.userId);
        return serviceTicketsList.push({
          ticketNumber: appConstants?.showServiceTicketId ? ticket?.ticketId : ticket?.ticketNumber || ticket?.ticketId,
          ticketCategory: ticket?.ticketCategory,
          status: ticket?.status,
          machineManufacturer: ticket?.manufacturer || "",
          machineModel: ticket?.model || "",
          machineSerialNumber: ticket?.serialNumber || "",
          machineMaintenanceCode: ticket?.maintenanceCode || "",
          description: ticket?.description || "",
          dueDate: moment(ticket.dueDate).format("DD MMM YYYY"),
          dateCreated: moment(ticket.openDate).format("DD MMM YYYY"),
          scheduledDate: moment(ticket.scheduledDate).format("DD MMM YYYY"),
          technician: assigneUser?.name,
          assigner: assignerUser?.nameEmpno || "",
          creator: creatorUser?.name || "",
        });
      });
    }
    const headers = [
      "Ticket ID, Open Date, Scheduled Date, Maintenance Code, Description, Manufacturer,Model, Serial Number, Creator, Supervisor, Technician, Category, Status",
    ];
    const keys = [
      "ticketNumber",
      "dateCreated",
      "scheduledDate",
      "machineMaintenanceCode",
      "description",
      "machineManufacturer",
      "machineModel",
      "machineSerialNumber",
      "creator",
      "assigner",
      "technician",
      "ticketCategory",
      "status",
    ];

    exportToCsv(headers, keys, exportAll ? serviceTicketsList : gridRows, "ServiceTickets");
    setShowExportAlertDialog(false);
  };

  const filterTickets = (filterOption, filterItem) => {
    setFilterOptions({ ...filterOptions, [filterOption]: filterOptions[filterOption].filter((item) => item !== filterItem) });
  };

  const FILTER_LABELS = {
    creator: "Creator",
    assigner: "Supervisor",
    technician: "Tech",
    maintenanceCode: "Maint. Code",
    category: "Category",
    status: "Status",
    manufModel: "Manf & Model",
    vendor: "Vendor",
  };

  const actionButtonSx = {
    minHeight: 34,
    px: 2,
    fontSize: "0.875rem",
    fontWeight: 400,
    lineHeight: 1.2,
    textTransform: "none",
    whiteSpace: "nowrap",
  };

  const renderAppliedHeaderFilters = () => {
    const entries = Object.entries(filters).filter(([, value]) => value !== "");
    if (entries.length === 0) return null;
    return (
      <Paper>
        <Stack direction={"row"} flexWrap={"wrap"} alignItems={"center"} px={0.5} py={filterOptions && filterOptions.overDue ? 0 : 0.3}>
          {entries.map(([key, value]) => {
            const displayValue =
              key === "category" && appConstants?.allTypes?.ABBREVIATIONS?.[value] ? appConstants.allTypes.ABBREVIATIONS[value] : value;
            return (
              <React.Fragment key={key}>
                <Typography m={1} textTransform={"capitalize"}>
                  {FILTER_LABELS[key]}
                </Typography>
                <Stack direction={"row"} alignItems={"center"} pl={1} borderRadius={10} backgroundColor="#00808030">
                  <Typography variant="caption">{displayValue}</Typography>
                  <IconButton onClick={() => setFilters((prev) => ({ ...prev, [key]: "" }))}>
                    <Tooltip title="Clear item">
                      <CloseIcon sx={{ fontSize: 15 }} />
                    </Tooltip>
                  </IconButton>
                </Stack>
              </React.Fragment>
            );
          })}
        </Stack>
      </Paper>
    );
  };

  const renderCustomFilter = (filterOption) => (
    <Paper>
      {filterOptions &&
      filterOptions[filterOption] &&
      filterOptions[filterOption].length &&
      filterOption !== "category" &&
      filterOption !== "status" &&
      filterOption !== "maintenanceCode" &&
      filterOption !== "ticketIds" &&
      filterOption !== "priority" ? (
        <Stack direction={"row"} flexWrap={"wrap"} alignItems={"center"} px={0.5} py={filterOptions && filterOptions.overDue ? 0 : 0.3}>
          <Typography m={1} textTransform={"capitalize"}>
            {filterOption}
          </Typography>
          {filterOptions[filterOption].map((filterItem) => (
            <Stack direction={"row"} alignItems={"center"} pl={1} borderRadius={10} backgroundColor="#00808030">
              <Typography variant="caption">{filterItem}</Typography>
              <IconButton onClick={() => filterTickets(filterOption, filterItem)}>
                <Tooltip title="Clear item">
                  <CloseIcon sx={{ fontSize: 15 }} />
                </Tooltip>
              </IconButton>
            </Stack>
          ))}
        </Stack>
      ) : null}
    </Paper>
  );

  return (
    <>
      <Box sx={{ mx: 2 }}>
        <Stack direction={"row"} justifyContent={"space-between"} my={2}>
          <Stack direction={"row"} flexWrap={"wrap"} alignItems={"center"} gap={2}>
            <Box>
              <Button
                startIcon={<AddIcon />}
                sx={actionButtonSx}
                autoCapitalize="none"
                variant="contained"
                onClick={() =>
                  navigate(`/${Links.CREATE_SERVICE_TICKET}/`, {
                    state: { isCreatingTicket: true },
                  })
                }
              >
                Create
              </Button>
            </Box>

            <OutlinedInput
              id="outlined-adornment-password"
              value={searchValue}
              onChange={(event) => handleSearchChange(event)}
              placeholder="Search..."
              startAdornment={
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              }
              endAdornment={
                searchValue && (
                  <InputAdornment position="end">
                    <IconButton onClick={handleClearSearch} edge="end">
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }
              sx={{ minWidth: 300, maxWidth: 600, height: 35 }}
            />

            <Tooltip title={filterOptions?.overDue ? "Clear over due filter" : "Filter over due tickets"}>
              <Button
                variant="contained"
                onClick={() => setFilterOptions({ ...filterOptions, overDue: !filterOptions?.overDue })}
                endIcon={filterOptions?.overDue ? <CloseIcon /> : null}
                sx={{
                  ...actionButtonSx,
                  ...(filterOptions?.overDue
                    ? {
                        backgroundColor: mode === "dark" ? theme.palette.grey[700] : theme.palette.primary[700],
                      }
                    : {}),
                }}
              >
                Over due
              </Button>
            </Tooltip>
            <Box>
              <Button
                startIcon={<DownloadIcon />}
                sx={actionButtonSx}
                autoCapitalize="none"
                variant="contained"
                onClick={() => {
                  const current = moment().startOf("month").format("YYYY-MM-DD");
                  setScheduleMonthValue(current);
                  setScheduleDialogOpen(true);
                }}
              >
                Schedule
              </Button>
            </Box>
            <Button startIcon={<CloseIcon />} sx={actionButtonSx} autoCapitalize="none" variant="contained" onClick={clearAllFilters}>
              Clear Filters
            </Button>
            {renderAppliedHeaderFilters()}
            {Object.keys(filterOptions).map((filterOption) => renderCustomFilter(filterOption))}
          </Stack>
          <Stack>
            <Button
              startIcon={<DownloadIcon />}
              sx={{ ...actionButtonSx, minWidth: 170 }}
              autoCapitalize="none"
              variant="contained"
              disabled={!gridRows?.length}
              onClick={(e) => {
                if (hasActiveFilters()) setShowExportAlertDialog(true);
                else downloadCsv(e);
              }}
            >
              Export to CSV
            </Button>
          </Stack>
        </Stack>

        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs value={selectedTab} onChange={handleTabsChange} aria-label="service tickets tabs" centered>
            <Tab
              sx={{
                fontWeight: "bold",
                marginRight: "20px",
                "&:hover": {
                  backgroundColor: "initial",
                  color: "initial",
                },
              }}
              label={`60 days (${filteredCounts.all})`}
              value={1}
            />
            <Tab
              sx={{
                fontWeight: "bold",
                marginRight: "20px",
                "&:hover": {
                  backgroundColor: "initial",
                  color: "initial",
                },
              }}
              label={`Periodic Service Tickets (${filteredCounts.periodic})`}
              value={2}
            />
            <Tab
              sx={{
                fontWeight: "bold",
                marginRight: "20px",
                "&:hover": {
                  backgroundColor: "initial",
                  color: "initial",
                },
              }}
              label={`One Time Service Tickets (${filteredCounts.oneTime})`}
              value={3}
            />
            <Tab
              sx={{
                fontWeight: "bold",
                marginRight: "20px",
                "&:hover": {
                  backgroundColor: "initial",
                  color: "initial",
                },
              }}
              label={`More Than 60 Days (${filteredCounts.old})`}
              value={4}
            />
          </Tabs>
        </Box>
        <>
          <Box style={{ height: 700, width: "100%", paddingTop: 3, marginTop: 8, overflow: "auto" }}>
            {loading || isLoading ? (
              <TableSkeleton rows={8} columns={columnAttributes.length} />
            ) : (
              <DataGrid
                loading={loading || isLoading}
                getRowId={(row) => row.ticketId}
                rows={gridRows}
                columns={columnAttributes}
                pageSize={5}
                rowsPerPageOptions={[5]}
                disableColumnMenu
                localeText={{ noRowsLabel: "No tickets." }}
                sx={{
                  "& .MuiDataGrid-columnHeaderTitle": {
                    fontWeight: "bold",
                  },
                }}
              />
            )}
          </Box>
        </>
        <Dialog open={showExportAlertDialog} onClose={() => setShowExportAlertDialog(false)}>
          <DialogContent>
            <Typography mt={1}>Click "Export filtered" to export only the filtered tickets.</Typography>
            <Typography mt={1}>Click "Export all" to export all tickets.</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowExportAlertDialog(false)}>Cancel</Button>
            <Button variant="contained" onClick={(e) => downloadCsv(e)}>
              Export filtered
            </Button>
            <Button variant="contained" onClick={(e) => downloadCsv(e, true)}>
              Export all
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog open={scheduleDialogOpen} onClose={() => setScheduleDialogOpen(false)} maxWidth="xs" fullWidth>
          <DialogContent sx={{ pt: 3 }}>
            <Typography sx={{ fontWeight: 700, mb: 1.5 }}>Download scheduled tickets</Typography>
            <FormControl fullWidth sx={{ mb: 1 }}>
              <InputLabel>Select month</InputLabel>

              <Select value={scheduleMonthValue} label="Select month" onChange={(e) => setScheduleMonthValue(e.target.value)}>
                {scheduleMonthOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary">
              Includes only tickets in status "Assigned" and "Assign Technician".
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setScheduleDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={() => {
                setScheduleDialogOpen(false);
                generateScheduledTicketsPdf(scheduleMonthValue);
              }}
            >
              Submit
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};
export default ServiceTicketsTab;
